import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeaders() {
  const password = sessionStorage.getItem('admin_password');
  if (!password) return {};
  return {
    Authorization: `Basic ${btoa(':' + password)}`
  };
}

export default function ProjectsManager({ onRefresh }) {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: '',
    featured: false
  });
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/categories`)
      ]);
      setProjects(projectsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${API}/projects`, payload, { headers: getAuthHeaders() });
        toast.success('Project created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { headers: getAuthHeaders() });
      toast.success('Project deleted');
      fetchData();
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      category: project.category,
      tags: project.tags?.join(', ') || '',
      featured: project.featured
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      tags: '',
      featured: false
    });
    setEditingProject(null);
  };

  const handleImageUpload = async (projectId, files) => {
    if (!files || files.length === 0) return;
    
    try {
      setUploadingImages(true);
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });

      await axios.post(`${API}/projects/${projectId}/images`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Images uploaded successfully');
      fetchData();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (projectId, imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/images/${imageId}`, { headers: getAuthHeaders() });
      toast.success('Image deleted');
      fetchData();
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  return (
    <div className="space-y-6" data-testid="projects-manager">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Projects</h2>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-hover" style={{ background: '#b8d71b', color: '#1a1a1a' }} data-testid="add-project-btn">
              <Plus size={20} className="mr-2" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProject ? 'Edit Project' : 'Create New Project'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Project Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="project-title-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  required
                  data-testid="project-description-input"
                />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                  <SelectTrigger data-testid="project-category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="modern, sustainable, urban"
                  data-testid="project-tags-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                  data-testid="project-featured-switch"
                />
                <Label>Featured Project</Label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="btn-hover" style={{ background: '#b8d71b', color: '#1a1a1a' }} data-testid="save-project-btn">
                  {editingProject ? 'Update' : 'Create'} Project
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-project-btn">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Projects List */}
      <div className="space-y-4">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="skeleton h-64" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-400">No projects yet. Create your first project!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((project) => (
              <Card key={project.id} className="p-6 hover:shadow-lg transition-shadow" data-testid={`project-card-${project.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {project.title}
                    </h3>
                    <p className="text-sm text-gray-500">{project.category}</p>
                    {project.featured && (
                      <span className="inline-block mt-2 px-2 py-1 text-xs rounded" style={{ background: '#b8d71b', color: '#1a1a1a' }}>
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(project)} data-testid={`edit-project-${project.id}`}>
                      <Edit size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(project.id)} data-testid={`delete-project-${project.id}`}>
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                
                {/* Images */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{project.images?.length || 0} / 10 Images</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => document.getElementById(`upload-${project.id}`).click()}
                      disabled={uploadingImages || (project.images?.length || 0) >= 10}
                      data-testid={`upload-images-${project.id}`}
                    >
                      <Upload size={14} className="mr-1" />
                      Upload
                    </Button>
                    <input
                      id={`upload-${project.id}`}
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(project.id, e.target.files)}
                    />
                  </div>
                  {project.images?.length > 0 && (
                    <div className="grid grid-cols-5 gap-2">
                      {project.images.map((img) => (
                        <div key={img.id} className="relative group aspect-square rounded overflow-hidden">
                          <img
                            src={img.thumb_url}
                            alt={img.filename}
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => handleDeleteImage(project.id, img.id)}
                            className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`delete-image-${img.id}`}
                          >
                            <Trash2 size={16} className="text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}